import { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import Overlay from "../../Componentes/Overlay";
import Modal from "../../Componentes/Utils/Modal";
import SortableTable from "../../Componentes/Utils/SortableTable";
import { useModal } from "../../contexts/ModalContext";
import { useToast } from "../../contexts/ToastContext";
import useApi from "../../hooks/useApi";
import ModalGerenciarObrasUsuario, {
    MODAL_ID as MODAL_OBRAS_USUARIO_ID,
} from "./ModalGerenciarObrasUsuario";

export const MODAL_ID = "modalAcessosObrasFolha";

const LIST_USUARIOS_URL = "/Painel/API/FolhaPag/AcessosObras/ListUsuarios/";

const Container = styled.div`
    width: 100%;
    height: min(70dvh, 640px);
    display: flex;
    flex-direction: column;
    gap: 12px;
    box-sizing: border-box;
    overflow: hidden;
`;

const Titulo = styled.h2`
    margin: 0;
    font-size: 18px;
    color: #263b4b;
    flex-shrink: 0;
`;

const Subtitulo = styled.p`
    margin: 0;
    font-size: 13px;
    color: #64748b;
    flex-shrink: 0;
`;

const HeaderBar = styled.div`
    display: flex;
    gap: 10px;
    align-items: center;
    flex-wrap: wrap;
    flex-shrink: 0;
`;

const Badge = styled.div`
    background: #f1f5f9;
    border: 1px solid #e2e8f0;
    color: #334155;
    padding: 8px 10px;
    border-radius: 8px;
    font-size: 13px;
    white-space: nowrap;
`;

const FilterInput = styled.input`
    height: 38px;
    flex: 1;
    min-width: 220px;
    border-radius: 8px;
    border: 1px solid #cbd5e1;
    background: #fff;
    color: #1e293b;
    padding: 0 12px;
    box-sizing: border-box;
    outline: none;
    font-size: 13px;

    &::placeholder {
        color: #94a3b8;
    }

    &:focus {
        border-color: #0f766e;
        box-shadow: 0 0 0 2px rgba(15, 118, 110, 0.2);
    }
`;

const TableShell = styled.div`
    flex: 1;
    min-height: 0;
    width: 100%;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    overflow: hidden;
    display: flex;
    flex-direction: column;

    & > div {
        flex: 1;
        min-height: 0;
    }
`;

function buildSearchHaystack(row) {
    if (!row) return "";

    return [row.usuario, row.user_id, row.email]
        .filter((v) => v != null && String(v).trim() !== "")
        .join(" ")
        .toUpperCase();
}

/**
 * Modal de gestão de acessos a obras (user_access) da Folha.
 * Lista usuários com permissão rh.folha-pag.access (ou admin).
 * Clique na linha abre o modal de obras daquele usuário.
 */
const ModalAcessosObrasFolha = () => {
    const api = useApi();
    const { closeModal, openModal, isModalOpen } = useModal();
    const { notifyError } = useToast();

    const [loading, setLoading] = useState(true);
    const [usuarios, setUsuarios] = useState([]);
    const [q, setQ] = useState("");
    const [usuarioSelecionado, setUsuarioSelecionado] = useState(null);

    const fetchUsuarios = useCallback(async () => {
        setLoading(true);

        try {
            const { data } = await api.get(LIST_USUARIOS_URL);
            setUsuarios(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error(error);
            setUsuarios([]);
            notifyError(error?.response?.data?.message || "Erro ao carregar usuários da Folha.");
        } finally {
            setLoading(false);
        }
    }, [api, notifyError]);

    useEffect(() => {
        fetchUsuarios();
    }, [fetchUsuarios]);

    const filtered = useMemo(() => {
        const query = q.trim().toUpperCase();
        if (!query) return usuarios;

        return usuarios.filter((row) => buildSearchHaystack(row).includes(query));
    }, [usuarios, q]);

    const handleOpenUsuario = useCallback(
        (row) => {
            if (!row?.id) return;
            setUsuarioSelecionado(row);
            openModal(MODAL_OBRAS_USUARIO_ID);
        },
        [openModal]
    );

    const columns = useMemo(
        () => [
            {
                id: "usuario",
                header: "NOME",
                accessor: "usuario",
                sortable: true,
                render: (value) =>
                    value != null && String(value).trim() !== "" ? String(value).trim() : "—",
            },
            {
                id: "user_id",
                header: "USUÁRIO",
                accessor: "user_id",
                sortable: true,
                width: "160px",
                render: (value) =>
                    value != null && String(value).trim() !== "" ? String(value).trim() : "—",
            },
            {
                id: "email",
                header: "E-MAIL",
                accessor: "email",
                sortable: true,
                render: (value) =>
                    value != null && String(value).trim() !== "" ? String(value).trim() : "—",
            },
        ],
        []
    );

    return (
        <>
            <Overlay state={true} changeContent={closeModal}>
                <Modal header close={closeModal} size="lg" blockOverflow>
                    <Container>
                        <Titulo>ACESSOS A OBRAS</Titulo>
                        <Subtitulo>
                            Usuários com permissão de acesso à Folha de Pagamento. Clique em um
                            usuário para gerenciar as obras liberadas.
                        </Subtitulo>

                        <HeaderBar>
                            <Badge>Total: {loading ? "..." : filtered.length}</Badge>
                            <FilterInput
                                placeholder="Pesquisar por nome, usuário ou e-mail..."
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                            />
                        </HeaderBar>

                        <TableShell>
                            <SortableTable
                                columns={columns}
                                data={filtered}
                                getRowId={(row) => row.id}
                                isLoading={loading}
                                loadingText="Carregando usuários..."
                                emptyText="NENHUM USUÁRIO COM ACESSO À FOLHA"
                                initialSort={{ key: "usuario", direction: "asc" }}
                                onRowClick={handleOpenUsuario}
                            />
                        </TableShell>
                    </Container>
                </Modal>
            </Overlay>

            {isModalOpen(MODAL_OBRAS_USUARIO_ID) && usuarioSelecionado && (
                <ModalGerenciarObrasUsuario usuario={usuarioSelecionado} />
            )}
        </>
    );
};

export default ModalAcessosObrasFolha;
